import React, { useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { mockData, generateWhatsAppLink } from '../mock';
import Header from '../components/Header';
import Seo from '../components/Seo';
import { SITE_URL, SITE_NAME } from '../lib/seo/constants';
import { useCatalogueResource } from '../hooks/useCatalogueResource';
import BlogPostHeader from '../components/blog/BlogPostHeader';
import BlogPostContent from '../components/blog/BlogPostContent';
import RelatedPosts from '../components/blog/RelatedPosts';
import { getBlogContent } from '../components/blog/blogContent';

const RELATED_POST_LIMIT = 3;

const BlogPost = () => {
  const { slug } = useParams();
  const navigate = useNavigate();

  // Local fallback derived synchronously from mockData
  const localPost = useMemo(
    () => mockData.blogPosts.find((p) => p.slug === slug),
    [slug]
  );

  // Try backend post; fall back to local mock
  const { data: post } = useCatalogueResource(`/api/blog/${slug}`, localPost);

  // All posts (for related-posts logic) — backend list with fallback
  const { data: allPosts } = useCatalogueResource('/api/blog', mockData.blogPosts);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  const handleShare = useCallback(async () => {
    const url = window.location.href;
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Link copied to clipboard.');
    } catch (err) {
      toast.error('Could not copy link. Please copy it manually.');
    }
  }, []);

  if (!post) {
    return (
      <div
        className="min-h-screen pt-32 pb-16"
        data-testid="blog-post-not-found"
      >
        <Header />
        <div className="container text-center">
          <h1 className="section-title">Post Not Found</h1>
          <p className="section-subtitle mt-4">
            The article you're looking for doesn't exist.
          </p>
          <Button
            className="btn-primary mt-8"
            onClick={() => navigate('/blog')}
          >
            <ArrowLeft className="mr-2" size={18} aria-hidden="true" />
            Back to Blog
          </Button>
        </div>
      </div>
    );
  }

  const relatedPosts = (allPosts || [])
    .filter((p) => p.category === post.category && p.id !== post.id)
    .slice(0, RELATED_POST_LIMIT);

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: post.title,
    description: post.excerpt,
    image: post.image,
    datePublished: post.date,
    dateModified: post.date,
    author: { '@type': 'Organization', name: post.author || SITE_NAME },
    publisher: {
      '@type': 'Organization',
      name: SITE_NAME,
      logo: { '@type': 'ImageObject', url: `${SITE_URL}/images/zeeva-logo.png` },
    },
    mainEntityOfPage: { '@type': 'WebPage', '@id': `${SITE_URL}/blog/${post.slug}` },
    articleSection: post.category,
  };

  return (
    <div className="min-h-screen pt-32 pb-16" data-testid="blog-post-page">
      <Seo
        title={post.title}
        description={post.excerpt}
        path={`/blog/${post.slug}`}
        image={post.image}
        type="article"
        publishedTime={post.date}
        modifiedTime={post.date}
        author={post.author}
        structuredData={articleSchema}
      />
      <Header />

      <div className="container mb-8">
        <Button
          className="btn-secondary"
          onClick={() => navigate('/blog')}
          data-testid="blog-post-back-btn"
        >
          <ArrowLeft className="mr-2" size={18} aria-hidden="true" />
          Back to Blog
        </Button>
      </div>

      <article className="container">
        <div className="max-w-4xl mx-auto">
          <BlogPostHeader post={post} onShare={handleShare} />
          <BlogPostContent content={getBlogContent(post)} />

          <div className="article-cta-box mt-12">
            <h3 className="text-2xl font-bold mb-4">
              Ready to Build Lifelong Capability?
            </h3>
            <p className="text-lg mb-6">
              Take the Zeeva Capability Assessment and get matched to the
              program designed for your goals.
            </p>
            <div className="flex gap-4 flex-wrap">
              <Button
                className="btn-cta"
                onClick={() => navigate('/assessment')}
                data-testid="blog-post-cta-assessment"
              >
                Start Your Assessment
              </Button>
              <Button
                className="btn-secondary"
                onClick={() =>
                  window.open(
                    generateWhatsAppLink(`I read "${post.title}" and want to learn more`),
                    '_blank',
                    'noopener,noreferrer'
                  )
                }
                data-testid="blog-post-cta-whatsapp"
              >
                Chat on WhatsApp
              </Button>
            </div>
          </div>
        </div>
      </article>

      <RelatedPosts posts={relatedPosts} />
    </div>
  );
};

export default BlogPost;
